import React, { useState, useRef } from "react";
import { Dropdown } from "react-bootstrap";

// Color palettes for each theme
const COLOR_THEMES = [
    // 🌿 Nature / Calm

    { name: "Forest", colors: ["#064e3b", "#10b981", "#6ee7b7"] },
    { name: "Ocean", colors: ["#0c4a6e", "#0284c7", "#38bdf8"] },
    { name: "Mint", colors: ["#064e3b", "#34d399", "#a7f3d0"] },
    { name: "Earth", colors: ["#78350f", "#d97706", "#fde68a"] },
    {name: "Layered Navy",colors: ["#10255A", "#59688E", "#F3905F"]},

    // 🌅 Warm / Vibrant
    { name: "Sunset", colors: ["#7c2d12", "#f97316", "#fed7aa"] },
    { name: "Rose", colors: ["#9f1239", "#fb7185", "#fecdd3"] },
    { name: "Coral", colors: ["#be123c", "#fb7185", "#fda4af"] },
    { name: "Amber", colors: ["#92400e", "#f59e0b", "#fde68a"] },

    // ❄️ Cool / Modern
    { name: "Ice", colors: ["#1e3a8a", "#60a5fa", "#bfdbfe"] },
    { name: "Steel", colors: ["#111827", "#4b5563", "#9ca3af"] },
    { name: "Slate", colors: ["#020617", "#475569", "#cbd5e1"] },

    // 🌌 Dark / Premium
    { name: "Midnight", colors: ["#020617", "#1e293b", "#38bdf8"] },
    { name: "Cosmic", colors: ["#312e81", "#6366f1", "#a5b4fc"] },
    { name: "Neon", colors: ["#0f172a", "#22d3ee", "#a855f7"] },
    { name: "Glacier", colors: ["#90cdf4", "#60a5fa", "#3b82f6"] },
    { name: "Harvest", colors: ["#fb923c", "#fbbf24", "#facc15"] },
    { name: "Lavender", colors: ["#a78bfa", "#c4b5fd", "#8b5cf6"] },
    { name: "Brutalist", colors: ["#000000", "#f97316", "#14b8a6"] },
    { name: "Obsidian", colors: ["#374151", "#6b7280", "#9ca3af"] },
    { name: "Orchid", colors: ["#ec4899", "#f472b6", "#f9a8d4"] },
    { name: "Solar", colors: ["#f97316", "#fbbf24", "#ea580c"] },
];

const ColorPaletteSelector = ({ selectedPalette, onPaletteSelect }) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [showDropdown, setShowDropdown] = useState(false);
    const [selectedTheme, setSelectedTheme] = useState(null);
    const dropdownRef = useRef(null);

    // Add custom scrollbar styles
    React.useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            .palette-scroll-container::-webkit-scrollbar {
                width: 6px;
            }
            .palette-scroll-container::-webkit-scrollbar-track {
                background: transparent;
            }
            .palette-scroll-container::-webkit-scrollbar-thumb {
                background: #cbd5e1;
                border-radius: 3px;
            }
            .palette-scroll-container::-webkit-scrollbar-thumb:hover {
                background: #94a3b8;
            }
        `;
        document.head.appendChild(style);
        return () => document.head.removeChild(style);
    }, []);

    const filteredThemes = COLOR_THEMES.filter(theme =>
        theme.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handlePaletteSelect = (theme) => {
        onPaletteSelect?.(theme);
        setSelectedTheme(theme);
        setShowDropdown(false);
        setSearchQuery("");
    };

    return (
        <Dropdown
            show={showDropdown}
            onToggle={(isOpen) => setShowDropdown(isOpen)}
            drop="up"
            ref={dropdownRef}
        >
            <Dropdown.Toggle
                variant="light"
                id="palette-selector-dropdown"
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '4px 10px',
                    backgroundColor: '#f0f3f5',
                    border: 'none',
                    borderRadius: '20px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#475569',
                    boxShadow: 'none'
                }}
                className="border-0 shadow-none"
            >
                <span className="font11" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>{selectedTheme?.colors?.length > 0 ? selectedTheme.colors.map((color, index) => (
                    <div><div
                        key={index}
                        style={{
                            width: '13px',
                            height: '13px',
                            borderRadius: '50%',
                            backgroundColor: color,
                            border: '2px solid white',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                        }}
                    />
                    </div>
                )) : "Theme"} 
                {selectedTheme?.colors?.length > 0 && <i className="fa-solid fa-times ms-2" 
                style={{ fontSize: '13px', cursor: 'pointer' }} onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onPaletteSelect?.(null);
                    setSelectedTheme(null);
                }}></i>}
                </span>
            </Dropdown.Toggle>

            <Dropdown.Menu
                style={{
                    minWidth: '280px',
                    maxWidth: '320px',
                    padding: '0',
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                    overflow: 'hidden',
                    backgroundColor: 'white'
                }}
                align="end"
            >
                {/* Search Bar */}
                <div style={{
                    padding: '12px',
                    borderBottom: '1px solid #f1f5f9',
                    backgroundColor: '#fafbfc'
                }}>
                    <div style={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center'
                    }}>
                        <i className="fa-solid fa-magnifying-glass" style={{
                            position: 'absolute',
                            left: '12px',
                            color: '#94a3b8',
                            fontSize: '14px'
                        }}></i>
                        <input
                            type="text"
                            placeholder="Search themes..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '8px 12px 8px 36px',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                fontSize: '14px',
                                outline: 'none',
                                backgroundColor: 'white'
                            }}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                </div>

                {/* Default themes section */}
                <div 
                    style={{
                        padding: '8px 0',
                        maxHeight: '300px',
                        paddingBottom: '80px',
                        overflowY: 'auto'
                    }}
                    className="palette-scroll-container"
                >
                    <div style={{
                        padding: '8px 16px',
                        fontSize: '12px',
                        fontWeight: '600',
                        color: '#64748b',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                    }}>
                        Default themes
                    </div>

                    {filteredThemes.map((theme) => (
                        <div
                            key={theme.name}
                            onClick={() => handlePaletteSelect(theme)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '10px 16px',
                                cursor: 'pointer',
                                backgroundColor: selectedPalette?.name === theme.name ? '#e0f2fe' : 'transparent',
                                transition: 'background-color 0.15s ease',
                                borderLeft: selectedPalette?.name === theme.name ? '3px solid #2196f3' : '3px solid transparent'
                            }}
                            onMouseEnter={(e) => {
                                if (selectedPalette?.name !== theme.name) {
                                    e.currentTarget.style.backgroundColor = '#f8fafc';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (selectedPalette?.name !== theme.name) {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                }
                            }}
                        >
                            <span style={{
                                fontSize: '14px',
                                fontWeight: '500',
                                color: '#1e293b',
                                flex: 1
                            }}>
                                {theme.name}
                            </span>
                            <div style={{
                                display: 'flex',
                                gap: '2px',
                                alignItems: 'center'
                            }}>
                                {theme.colors.map((color, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            width: '20px',
                                            height: '20px',
                                            borderRadius: '50%',
                                            backgroundColor: color,
                                            border: '2px solid white',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                        }}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </Dropdown.Menu>
        </Dropdown>
    );
};

export default ColorPaletteSelector;
